<script lang="ts">
  import { session, currentStep, records, selectedChecklistId, tempSelectedRole, tempSelectedName, centerClockVisible } from '../stores';
  import { onMount, onDestroy } from 'svelte';
  import { get } from 'svelte/store';
  import { checklists } from '../data/checklists';
  import { ROLE_ORDER, ROLE_CHECKLIST_ORDER } from '../data/role_order';
  import type { RoleType, UserSession } from '../types';
  import { ROLE_LABELS } from '../types';

  let selectedRole: RoleType | null = null;
  let staffName: string = '';
  let showRoleSelect = true;
  // 中央時鐘文字與 interval 句柄
  let centerTimeText = '--:--:--';
  let centerInterval: ReturnType<typeof setInterval> | null = null;

  // 角色順序由 data/role_checklist_map.csv 決定，透過 ROLE_ORDER 匯入
  const roles: RoleType[] = ROLE_ORDER;

  onMount(() => {
    const unsubSession = session.subscribe(s => {
      if (s && s.role) {
        selectedRole = s.role;
        staffName = s.name ?? '';
        // 當已建立 session 時，也將值同步備份至暫存名稱
        tempSelectedName.set(staffName || '');
        showRoleSelect = false;
      } else {
        showRoleSelect = true;
        selectedRole = null;
        // 保留使用者在輸入欄位中已輸入的稱呼值，以免按下「重設」清除剛剛輸入的內容。
        // 只有使用者明確按下清除按鈕時才會清空此欄位。
      }
    });

    const unsubTemp = tempSelectedRole.subscribe(val => {
      if (val) {
        selectedRole = val;
        showRoleSelect = false;
      } else {
        // 若暫存選擇被清掉，只有在沒有已建立 session 時
        // 才回到選角色的狀態；若已存在 session，讓 session 的狀態
        // 決定是否顯示 checklist 列表，避免在切換到 checklist 後
        // 被 temp 清空時又跳回選角畫面。
        // 使用 get() 來同步讀取當前 session
        const s = get(session) as any;
        if (!s || !s.role) {
          showRoleSelect = true;
          selectedRole = null;
        }
      }
    });

    // 同步暫存名稱到本地變數
    const unsubName = tempSelectedName.subscribe(n => {
      staffName = n ?? '';
    });

    return () => { unsubSession(); unsubTemp(); unsubName(); };
  });

  // 當顯示 checklist 列表（selectedRole 且不是選角畫面）時，顯示中間時鐘並通知 header 隱藏時鐘
  $: centerClockVisible.set(!!(selectedRole && !showRoleSelect));

  $: if ($centerClockVisible) {
    const fmt = () => new Date().toLocaleString('zh-TW', {hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'});
    centerTimeText = fmt();
    if (!centerInterval) centerInterval = setInterval(() => { centerTimeText = fmt(); }, 1000);
  } else {
    if (centerInterval) { clearInterval(centerInterval); centerInterval = null; }
  }

  onDestroy(() => {
    if (centerInterval) { clearInterval(centerInterval); centerInterval = null; }
    centerClockVisible.set(false);
  });

  function selectRole(role: RoleType) {
    // 設定暫存選擇讓 header 顯示
    tempSelectedRole.set(role);
    selectedRole = role;
    showRoleSelect = false;
  }

  // 使用 header 的返回按鈕（handleSelectionBack）來清除暫存選擇並回到選單，
  // 因此此處不再需要本地的 goBack()。

  function selectChecklist(checklistId: string) {
    if (!selectedRole) return;

    const newSession: UserSession = {
      role: selectedRole,
      // 若使用者未輸入稱呼，則不設定 name 屬性（保持 undefined），避免之後顯示重複的預設文字
      name: staffName ? staffName : undefined,
      startTime: new Date().toISOString()
    };

    session.set(newSession);
    selectedChecklistId.set(checklistId);
    // 清除暫存選擇，避免 header 繼續顯示 tempSelectedRole
    tempSelectedRole.set(null);
    // 不清除 records，以便返回時保留勾選紀錄
    currentStep.set('checklist');
  }
  // Sort checklists by the order defined in role_checklist_map.csv for the current role.
  // Use ROLE_CHECKLIST_ORDER as the authoritative list: only show checklists whose id
  // appears in the order array, so committee can see 住戶須知 even though its role is 'resident'.
  function getSortedChecklists(role: RoleType) {
    const order = ROLE_CHECKLIST_ORDER[role] ?? [];
    const filtered = checklists.filter(c => order.includes(c.id));
    return filtered.sort((a, b) => {
      const ai = order.indexOf(a.id);
      const bi = order.indexOf(b.id);
      // 未在順序表中的項目排到最後（實際上不會發生，因為 filter 已確保在 order 內）
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }
</script>

<div class="w-full max-w-2xl mx-auto px-2">

  {#if showRoleSelect}
    <!-- 稱呼輸入放在選擇角色頁頂端，僅在未選角時顯示。因為提示文字較長且預設字型大，一律使用上下排版 (flex-col) 避免寬度不平衡，並限制輸入框最大寬度為 max-w-xs -->
    <div class="mb-6 p-4 bg-blue-50 rounded-2xl border-l-4 border-blue-500">
      <div class="flex flex-col gap-2">
        <label for="staff-name" class="text-xl sm:text-2xl font-semibold text-gray-700">
          稱呼（存入後不問數學題）
        </label>
        <div class="flex items-center gap-2 w-full max-w-xs">
          <input
            id="staff-name"
            type="text"
            bind:value={staffName}
            placeholder="稱呼"
            on:input={() => tempSelectedName.set(staffName)}
            class="flex-1 min-w-0 px-4 py-3 text-xl sm:text-2xl border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
          />
          <button type="button" on:click={() => { staffName = ''; tempSelectedName.set(''); }} class="ml-2 px-3 py-2 bg-white text-gray-600 rounded-md hover:bg-gray-100 shrink-0">
            ✕
          </button>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-4 sm:gap-6 mb-8">
      {#each roles as role (role)}
        <button
          on:click={() => selectRole(role)}
          class="p-6 sm:p-10 bg-linear-to-br rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 text-center font-bold text-white text-xl sm:text-3xl leading-tight"
          class:from-blue-500={role === 'committee'}
          class:to-blue-600={role === 'committee'}
          class:from-emerald-500={role === 'resident'}
          class:to-emerald-600={role === 'resident'}
          class:from-amber-500={role === 'day'}
          class:to-amber-600={role === 'day'}
          class:from-purple-500={role === 'night'}
          class:to-purple-600={role === 'night'}
        >
          {ROLE_LABELS[role]}
        </button>
      {/each}
    </div>
  {:else if selectedRole}
    <div class="mb-2">
      <div class="text-center mb-2">
        <div class="text-3xl sm:text-4xl font-bold text-gray-800">{centerTimeText}</div>
      </div>
      <div class="space-y-2">
          {#each getSortedChecklists(selectedRole) as checklist (checklist.id)}
            {@const items = checklist.items}
            {@const completedCount = items.filter(i => $records[i.id]).length}
            {@const unfinished = items.map((it, idx) => $records[it.id] ? null : idx + 1).filter(it => it !== null)}
            {@const completed = items.map((it, idx) => $records[it.id] ? idx + 1 : null).filter(it => it !== null)}
            {@const completedTimestamps = items
              .map(i => $records[i.id]?.timestamp)
              .filter((ts): ts is string => !!ts)
              .map(ts => new Date(ts).getTime())
              .filter(t => !isNaN(t))
            }
            {@const earliestTime = completedTimestamps.length > 0
              ? new Date(Math.min(...completedTimestamps)).toLocaleString('zh-TW', {hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})
              : null
            }
            {@const latestTime = completedTimestamps.length > 0
              ? new Date(Math.max(...completedTimestamps)).toLocaleString('zh-TW', {hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})
              : null
            }
            <button
              on:click={() => selectChecklist(checklist.id)}
              class="w-full p-1 sm:p-2 bg-linear-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border-2 border-blue-300 rounded-2xl transition-all duration-300 text-left active:scale-95"
            >
              <div class="flex items-center justify-between">
                <div>
                  <div class="text-xl sm:text-3xl font-bold text-blue-700">
                    {checklist.title}
                  </div>
                  <!-- Show time range of completed items to help verify records are from today -->
                  {#if earliestTime !== null && latestTime !== null}
                    <div class="text-xs sm:text-sm text-amber-700 font-medium mt-0.5">
                      {#if earliestTime === latestTime}
                        ✔ {earliestTime}
                      {:else}
                        ✔ {earliestTime} ～ {latestTime}
                      {/if}
                    </div>
                  {/if}
                </div>
                <div class="text-right shrink-0">
                  <div class="text-blue-700 font-bold text-base sm:text-xl whitespace-nowrap">
                    {completedCount}/{items.length} 已完成
                  </div>
                  <!-- Show completed item indices when fewer than 3 are done -->
                  {#if completedCount > 0 && completedCount <= 3}
                    <div class="text-xs sm:text-sm font-bold text-green-600 whitespace-nowrap">
                      {completed.join(',')} 已完成
                    </div>
                  {/if}
                  {#if unfinished.length > 0}
                    <div class="text-xs sm:text-sm font-bold text-red-600 whitespace-nowrap">
                      {unfinished.slice(0, 3).join(',')}{unfinished.length > 3 ? '...' : ''} 未完成
                    </div>
                  {/if}
                </div>
              </div>
            </button>
          {/each}
      </div>
    </div>
  {/if}
</div>
